import { Box, Divider, SimpleGrid, VStack } from '@chakra-ui/react';
import { PATH_NAME } from 'configs';
import { withPermission } from 'hocs/withPermission';
import { useState } from 'react';

import { colors } from 'themes/foundations/colors';

import { PartnerPermission } from 'types/permission';

import { Card, PrimaryButton, Typography } from 'components/atoms';
import { Table, ColumnsType } from 'components/organisms/Table';
import { Paginate } from 'components/molecules';

import { useRouter, useTranslate } from 'hooks/common';
import { useCreditPoolOverview } from 'hooks/components/credit';

import { LAYOUT_CONFIG } from 'configs/layout';

import { CreditSummaryItem } from './CreditSummaryItem';

const formatCredits = (value?: number | null) => Number(value ?? 0).toLocaleString('en-US');

export const CreditPoolOverviewWidget = () => {
  const t = useTranslate();
  const router = useRouter();
  const overview = useCreditPoolOverview();
  const companyPool = overview?.company_pool;
  const locations = overview?.locations ?? [];
  const showNegativeCreditDetails = Boolean(
    companyPool
      && companyPool.negative_credit_floor_limit !== 0
      && companyPool.available_credits < 0,
  );
  const [pagination, setPagination] = useState<Paginate>({
    current: 1,
    pageSize: 5,
  });

  const columns: ColumnsType<typeof locations[number]>[] = [
    {
      key: 'name',
      title: t('label.outletName'),
      dataIndex: 'name',
      width: '34%',
      pined: true,
      render: (value) => <Typography.Title fontSize="md">{value}</Typography.Title>,
    },
    {
      key: 'usable_credits',
      title: t('label.usableCredits'),
      dataIndex: 'usable_credits',
      width: '22%',
      cellStyleProps: { textAlign: 'center' },
      render: (value) => (
        <Typography.Title
          fontSize="md"
          color={Number(value ?? 0) < 0 ? colors.danger : undefined}
        >
          {formatCredits(value)}
        </Typography.Title>
      ),
    },
    {
      key: 'reserved_credits',
      title: t('label.reservedCredits'),
      dataIndex: 'reserved_credits',
      width: '22%',
      cellStyleProps: { textAlign: 'center' },
      render: (value) => <Typography.Text>{formatCredits(value)}</Typography.Text>,
    },
    {
      key: 'runway_days',
      title: t('label.estimatedDaysBeforeNextTopUp'),
      dataIndex: 'runway_days',
      width: '22%',
      cellStyleProps: { textAlign: 'center' },
      render: (value) => <Typography.Text>{value ?? t('label.notAvailable')}</Typography.Text>,
    },
  ];

  return (
    <Card
      round={LAYOUT_CONFIG.dashboard.round}
      boxShadow={LAYOUT_CONFIG.dashboard.boxShadow}
      p={{ base: '20px', lg: '20px 18px' }}
      w="100%"
      fitParent
    >
      <VStack align="stretch" spacing="18px">
        <Typography.Title fontSize={{ base: 'lg', lg: 'xl' }} uppercase>
          {t('title.creditOverview')}
        </Typography.Title>

        {companyPool && (
          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing="12px">
            {showNegativeCreditDetails ? (
              <>
                <CreditSummaryItem
                  label={t('label.creditsLeftToSpend')}
                  value={companyPool.credits_left_to_spend}
                />
                <CreditSummaryItem
                  label={t('label.negativeCreditFloorLimit')}
                  value={companyPool.negative_credit_floor_limit}
                />
              </>
            ) : (
              <CreditSummaryItem
                label={t('label.usableCredits')}
                value={companyPool.applicable_balance}
              />
            )}
          </SimpleGrid>
        )}

        {!!locations.length && (
          <Table
            columns={columns}
            data={locations}
            rowKey={(record) => record.location_id}
            pagination={pagination}
            onChange={(nextPagination, _sorter, _filter, action) => {
              if (action === 'pagination' && nextPagination) {
                setPagination(nextPagination);
              }
            }}
            hidePaginationOnSinglePage
            collapsible={false}
            maxHeight="360px"
            cardStyles={{ boxShadow: '0 3px 6px #f0761533' }}
          />
        )}

        {!locations.length && (
          <>
            <Divider />
            <Typography.Text>{t('message.noLocationsToShow')}</Typography.Text>
          </>
        )}

        <Box textAlign="right">
          <PrimaryButton onClick={() => router.push(PATH_NAME.JOD_CREDIT)}>
            {t('button.goToJodCreditsPage')}
          </PrimaryButton>
        </Box>
      </VStack>
    </Card>
  );
};

export const CreditPoolOverviewWidgetWithPermission = withPermission(
  PartnerPermission.VIEW_CREDIT_OVERVIEW,
  CreditPoolOverviewWidget,
);
